import { useState, useEffect } from 'react';
import EmployeeProjectForm from './EmployeeProjectForm';
import ProjectEmployeeList from './ProjectEmployeeList';
import Spinner from '../../Spinner';
import { useApi } from '../../../api/ApiProvider';
import Api from '../../../axiosApi/api';
import {
  CreateEmployeeProjectCommand,
  Employee,
  UpdateEmployeeProjectCommand,
} from 'axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { useDialog } from 'common/DialogProvider';
import { SCOPES } from 'common/permissions';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { convertDateAsUTC } from 'common/utils';
import intl from 'components/i18n/ReactIntlWrapper';
import { ALLOCATION_TYPES } from '../../../common/constants';
import ItemsSelect from '../../common/ItemsSelect';
import { Card, CardHeader, CardContent, Typography, Grid, Box } from '@mui/material';
import { handleAPIError } from '../../../common/errorHandler';
import { useSearch } from 'hooks/useSearch';

type EmployeeProjectProps = {
  idProject: number;
  disabled: boolean;
  errors: any;
  setErrors: any;
  onDataChange?: () => void; // Callback to trigger chart refresh
};

const EmployeeByProject: React.FC<EmployeeProjectProps> = ({ idProject, disabled, errors, setErrors, onDataChange }) => {
  const DefaultEmployeeProjectInput = {
    idEmployee: null,
    idProject: idProject,
    hourlyRate: 0,
    monthlyRate: 0,
    startDate: null,
    allocationType: 1,
    allocation: 0,
    dueDate: null,
    idRole: null,
    currentPage: 1,
    isTimesheetViewer: false,
  };

  const [input, setInput] = useState<
    CreateEmployeeProjectCommand | UpdateEmployeeProjectCommand | null
  >(DefaultEmployeeProjectInput);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const api: Api = useApi();

  const toast = useToastMessageQueue();

  const [employeesByProject, setEmployeesByProject] = useState({
    totalRecords: null,
    employeeProjects: [],
  });

  const [employees, setEmployees] = useState<Employee[]>(null);

  const [roles, setRoles] = useState(null);

  const [loading, setLoading] = useState<boolean>(false);

  const [inactiveEmployees, setInactiveEmployees] = useState(false);

  const [hiddenRole, setHiddenRole] = useState(false);

  const [loadingSaveEmployeeProject, setLoadingSaveEmployeeProject] = useState<boolean>(false);

  const [limit, setLimit] = useState<number>(() => {
    return parseInt(localStorage.getItem('itemsPerPageEmployeeProyect'), 10) || 10;
  });

  useEffect(() => {
    setLimit(parseInt(localStorage.getItem('itemsPerPageEmployeeProyect'), 10) || 10);
  }, []);

  const fetchEmployees = async () => {
    const response = await api.employeeApi
      .apiVversionEmployeeAllGet('1', 1, 100, false, {})
      .then((response) => {
        if (response.data.data) {
          setEmployees(response.data.data.queryResult);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  const fetchEmployeesByProject = async (idProject: number) => {
    const response = await api.projectApi
      .apiVversionEmployeeProjectGetByProjectIdIdGet(idProject, '1', {})
      .then((response) => {
        if (response.data.data) {
          setEmployeesByProject({
            totalRecords: response.data?.data?.totalRecords,
            employeeProjects: response.data?.data?.employeeProjects,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  const fetchRoles = async () => {
    const response = await api.projectApi
      .apiVversionListRolesGet('1')
      .then((response) => {
        if (response.data && 'queryResult' in response.data) {
          setRoles(response.data.queryResult);
        } else if (
          response.data &&
          'data' in response.data &&
          'queryResult' in response.data.data
        ) {
          setRoles(response.data.data.queryResult);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchEmployees(), fetchRoles()]).then(() => {
      setLoading(false);
    });
    fetchEmployees();
    fetchRoles();
    if (idProject) {
      fetchEmployeesByProject(idProject);
    }
  }, [idProject, limit]);

  const handleInputChange = (e) => {
    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });
    setErrors(
      validate({
        ...input,
        [e.target.name]: e.target.value,
      })
    );
  };

  const handleEmployeeChange = (e) => {
    const inputVal = {
      ...input,
      employee: { name: e.name, lastName: e.lastName },
      idEmployee: e.id,
    };
    setInput({ ...inputVal });
    setErrors(
      validate({
        ...input,
        employee: { name: e.name, lastName: e.lastName },
      })
    );
  };

  const handleRoleChange = (e) => {
    const selectedRoleId = e.target.value;
    const selectedRoleObj = roles?.find((role) => role.id === selectedRoleId);
    const inputVal = {
      ...input,
      idRole: selectedRoleId,
      Role: selectedRoleObj ? intl.get(selectedRoleObj.name) : '',
    };
    setInput({ ...inputVal });
    setErrors(
      validate({
        ...input,
        idRole: selectedRoleId,
      })
    );
  };

  const handleDueDate = (e) => {
    setInput({
      ...input,
      dueDate: e,
    });
    setErrors(
      validate({
        ...input,
        dueDate: e,
      })
    );
  };

  const handleStartDate = (e) => {
    setInput({
      ...input,
      startDate: e,
    });
    setErrors(
      validate({
        ...input,
        startDate: e,
      })
    );
  };

  const handleAllocationTypeChange = (e) => {
    setInput({
      ...input,
      allocationType: e.id,
    });
    setErrors(
      validate({
        ...input,
        allocationType: e.id,
      })
    );
  };

  const isEditMode = () => {
    return Object.keys(input).includes('id');
  };

  const validate = function (input) {
    let errors: any = {};

    if (!input.employee) {
      errors.employee = intl.get('validate.errors.isRequired');
    }
    if ((!input.hourlyRate && !input.monthlyRate) || input.hourlyRate === '') {
      errors.hourlyRate = intl.get('validate.errors.isRequired');
    } else if (input.hourlyRate < 0) {
      errors.hourlyRate = intl.get('validate.errors.positive');
    }
    if ((!input.monthlyRate && !input.hourlyRate) || input.monthlyRate === '') {
      errors.monthlyRate = intl.get('validate.errors.isRequired');
    } else if (input.monthlyRate < 0) {
      errors.monthlyRate = intl.get('validate.errors.positive');
    }
    if (input.hourlyRate && input.hourlyRate > 0 && input.monthlyRate && input.monthlyRate > 0) {
      errors.hourlyRate = intl.get('validate.errors.selectOnly');
      errors.monthlyRate = intl.get('validate.errors.selectOnly');
    }
    if (input.hourlyRate === '0' && input.monthlyRate === '0') {
      errors.hourlyRate = intl.get('validate.errors.selectOne');
      errors.monthlyRate = intl.get('validate.errors.selectOne');
    }
    if (!input.idRole) {
      errors.idRole = intl.get('validate.errors.isRequired');
    }
    if (!input.dueDate) {
      errors.dueDate = intl.get('validate.errors.isRequired');
    }
    if (!input.startDate) {
      errors.startDate = intl.get('validate.errors.isRequired');
    }
    if (input.allocation > 100 && input.allocationType == 1) {
      errors.allocationType = intl.get('validate.errors.percMore100');
    } else if (input.allocation < 0) {
      errors.allocationType = intl.get('validate.errors.positive');
    }
    if (!input.allocation && input.allocation !== 0) {
      errors.allocation = intl.get('validate.errors.isRequired');
    }
    setErrors({ ...errors });
    console.log('VALIDATE', errors, input);
    return Object.keys(errors).length > 0 ? errors : {};
  };

  const handleEmployeeEdit = (idEmployee: number) => {
    if (!isEditMode()) {
      const editingEmployee = employeesByProject?.employeeProjects.find(
        (employee: Employee) => employee.id == idEmployee
      );
      if (editingEmployee != null) {
        setInput({
          ...editingEmployee,
          id: idEmployee,
          currentPage: currentPage,
          dueDate: convertDateAsUTC(editingEmployee.dueDate as unknown as string),
          startDate: convertDateAsUTC(editingEmployee.startDate as unknown as string),
        });
        setErrors(
          validate({
            ...editingEmployee,
            id: idEmployee,
            dueDate: convertDateAsUTC(editingEmployee.dueDate as unknown as string),
            startDate: convertDateAsUTC(editingEmployee.startDate as unknown as string),
          })
        );
      } else toast.error({ message: intl.get('employeeProject.toast.error.handleEmployeeEdit') });
    } else {
      setInput(DefaultEmployeeProjectInput);
    }
  };

  const { openDialog, closeDialog } = useDialog();

  const handleEmployeeDelete = async (idEmployee: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('confirm.modal.employeeProject.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try {
            await api.projectApi.apiVversionEmployeeProjectIdDelete(idEmployee, '1', {});
            toast.success({
              message: intl.get('employeeProject.toast.success.handleEmployeeDelete'),
            });
            fetchEmployeesByProject(idProject);
            onDataChange?.(); // Trigger chart refresh
          } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
          } finally {
            closeDialog(dialogId);
          }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => {
          closeDialog(dialogId);
        },
      },
    });
    return dialogId;
  };

  const handleAddEmployeeProject = async (e) => {
    e.preventDefault();
    const errors = validate(input);
    if (JSON.stringify(errors) === JSON.stringify({})) {
      setLoadingSaveEmployeeProject(true);
      try {
        await api.projectApi.apiVversionEmployeeProjectPost('1', input, {});
        toast.success({
          message: intl.get('employeeProject.toast.success.handleAddEmployeeProject'),
        });
        setInput(DefaultEmployeeProjectInput);
        fetchEmployeesByProject(idProject);
        onDataChange?.(); // Trigger chart refresh
      } catch (error) {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      } finally {
        setLoadingSaveEmployeeProject(false);
      }
    }
  };

  const handleSaveEmployeeProject = async (e) => {
    e.preventDefault();
    const errors = validate(input);
    if (JSON.stringify(errors) === JSON.stringify({})) {
      try {
        setLoadingSaveEmployeeProject(true);
        setInactiveEmployees(false);
        await api.projectApi.apiVversionEmployeeProjectPut('1', input, {});
        toast.success({
          message: intl.get('employeeProject.toast.success.handleSaveEmployeeProject'),
        });
        setInput(DefaultEmployeeProjectInput);
        fetchEmployeesByProject(idProject);
        onDataChange?.(); // Trigger chart refresh
      } catch (error) {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      } finally {
        setLoadingSaveEmployeeProject(false);
      }
    }
    setSearchTerm('');
  };

  const handleCancelEdit = () => {
    setInput(DefaultEmployeeProjectInput);
  };

  const isEmployeeEditMode = (idEmployee: number) => {
    return isEditMode() && (input as UpdateEmployeeProjectCommand).id === idEmployee;
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" color="primary" id="employeeProject" sx={{ mb: 2 }}>
        {intl.get('employeeProject.header')}
      </Typography>
      <PermissionsGate
        viewScopes={[SCOPES['projects.employees.edit'], SCOPES['projects.employees.add']]}
        editScopes={[SCOPES['projects.employees.edit'], SCOPES['projects.employees.add']]}
        viewRoles={[]}
        editRoles={[]}
      >
        <EmployeeProjectForm
          loadingSaveEmployeeProject={loadingSaveEmployeeProject}
          handleEmployeeChange={handleEmployeeChange}
          employees={employees}
          selectedEmployee={input.idEmployee}
          handleInputChange={handleInputChange}
          handleRoleChange={handleRoleChange}
          selectedRole={input.idRole}
          roles={roles}
          handleStartDate={handleStartDate}
          handleDueDate={handleDueDate}
          handleAddEmployeeProject={handleAddEmployeeProject}
          isEditMode={isEditMode}
          handleSaveEmployeeProject={handleSaveEmployeeProject}
          handleCancelEdit={handleCancelEdit}
          input={input}
          errors={errors}
          disabled={disabled}
          handleAllocationTypeChange={handleAllocationTypeChange}
          selectedAllocationType={input.allocationType}
          allocationTypes={ALLOCATION_TYPES}
          setHiddenRole={setHiddenRole}
          setInput={setInput}
        />
      </PermissionsGate>

      <Box mt={2}>
        <>
          <ProjectEmployeeList
            data={employeesByProject}
            loadingEmployee={loading}
            input={input}
            setInput={setInput}
            inactiveEmployees={inactiveEmployees}
            setInactiveEmployees={setInactiveEmployees}
            handleEmployeeEdit={handleEmployeeEdit}
            disabled={disabled}
            handleEmployeeDelete={handleEmployeeDelete}
          />
        </>
      </Box>
    </Box>
  );
};

export default EmployeeByProject;
